import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { UserGroupDataDisplay } from "#src/components/Common/UserGroupDataDisplay";
import { useListCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import { useQuery } from "@tanstack/react-query";
import { useHasPermission } from "#src/contexts/AuthenticatedContext.helpers";
import {
  Accordion,
  KeyValueList,
  Skeleton,
} from "@validereinc/common-components";
import {
  UserGroupsAdapter,
  UsersAdapter,
  WorkflowUserTaskType,
  type UserGroupType,
  type UserType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import { isEmpty } from "es-toolkit/compat";
import React from "react";
import styles from "./WorkflowConfigurationDrawerAssignmentFilterDetails.module.scss";

const cx = classNames.bind(styles);

export const WorkflowConfigurationDrawerAssignmentFilterDetails = ({
  stepDefinition,
}: {
  stepDefinition?: WorkflowUserTaskType;
}) => {
  const [canReadUserGroups] = useHasPermission("user_groups:read");
  const assignmentFilter = stepDefinition?.assignment_filter ?? {};
  // assignmentFilter is currently restricted to having a single key
  const assignmentFilterKey = Object.keys(assignmentFilter)[0] ?? "";
  // key format: <assetType>.custom_attributes.<customAttributeFieldName>
  const [customAttributeEntityType, , customAttributeFieldName] =
    assignmentFilterKey.split(".") ?? "";

  const customAttributeQueryFilters = {
    entity_type: customAttributeEntityType,
    ...(customAttributeFieldName && { field_name: customAttributeFieldName }),
  };

  const { data: customAttributeData, isLoading: isCustomAttributesLoading } =
    useListCustomAttributes(
      {
        filters: customAttributeQueryFilters,
      },
      {
        enabled: Boolean(customAttributeFieldName),
      }
    );

  const customAttribute = customAttributeData?.data[0];

  const getAssignmentFilterAssigneeData = () => {
    const userIds = new Set<string>();
    const userGroupIds = new Set<string>();

    Object.values(assignmentFilter?.[assignmentFilterKey] ?? {}).forEach(
      (assignee) => {
        if (assignee.assignee_user) {
          userIds.add(assignee.assignee_user);
        } else if (assignee.assignee_group) {
          userGroupIds.add(assignee.assignee_group);
        }
      }
    );

    return {
      userIds: Array.from(userIds),
      userGroupIds: Array.from(userGroupIds),
    };
  };

  const { userIds, userGroupIds } = getAssignmentFilterAssigneeData();

  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    queryKey: ["users", { filters: { id: userIds } }],
    queryFn: () => UsersAdapter.getList({ filters: { id: userIds } }),
    enabled: Boolean(userIds.length),
  });
  const users = usersData?.data ?? [];
  const userIdsToUserDetailsMap = users.reduce<Record<string, UserType>>(
    (acc, user) => {
      acc[user.id] = user;

      return acc;
    },
    {}
  );

  const { data: userGroupsData, isLoading: isUserGroupsLoading } = useQuery({
    queryKey: ["users", "groups", { filters: { id: userGroupIds } }],
    queryFn: () =>
      UserGroupsAdapter.getList({
        filters: { id: userGroupIds },
      }),
    enabled: Boolean(userGroupIds.length) && canReadUserGroups,
  });
  const userGroups = userGroupsData?.data ?? [];
  const userGroupIdsToUserGroupDetailsMap = userGroups.reduce<
    Record<string, UserGroupType>
  >((acc, userGroup) => {
    acc[userGroup.id] = userGroup;

    return acc;
  }, {});

  const assignmentFilterKeyValueData =
    assignmentFilterKey && assignmentFilter[assignmentFilterKey]
      ? Object.entries(assignmentFilter[assignmentFilterKey]).map(
          ([customAttributeValue, assignee]) => ({
            title: customAttributeValue,
            value: assignee.assignee_user ? (
              <UserDataDisplay
                id={assignee.assignee_user}
                name={userIdsToUserDetailsMap[assignee.assignee_user]?.name}
                alignment="right"
              />
            ) : assignee.assignee_group ? (
              <UserGroupDataDisplay
                id={assignee.assignee_group}
                name={
                  userGroupIdsToUserGroupDetailsMap[assignee.assignee_group]
                    ?.name
                }
                alignment="right"
              />
            ) : null,
          })
        )
      : [];

  const isLoading =
    isCustomAttributesLoading ||
    (Boolean(userIds.length) && isUsersLoading) ||
    (Boolean(userGroupIds.length) && isUserGroupsLoading);

  if (isEmpty(assignmentFilter) || !assignmentFilterKey) return null;

  return (
    <>
      {!assignmentFilterKey.startsWith("$") ? (
        <Skeleton isLoading={isLoading}>
          <Accordion
            key={assignmentFilterKey}
            className={cx("accordion")}
            defaultActiveKeys={[assignmentFilterKey]}
          >
            <Accordion.AccordionPanel
              dataKey={assignmentFilterKey}
              className={cx("accordion-panel")}
              title={`Assignees by ${customAttribute?.display_name ?? customAttributeFieldName}`}
            >
              <Skeleton isLoading={isLoading}>
                <KeyValueList
                  data={assignmentFilterKeyValueData}
                  color="neutral"
                  variant="shaded"
                />
              </Skeleton>
            </Accordion.AccordionPanel>
          </Accordion>
        </Skeleton>
      ) : null}

      {assignmentFilterKey.startsWith("$") ? (
        <Accordion
          key={assignmentFilterKey}
          className={cx("accordion")}
          defaultActiveKeys={[assignmentFilterKey]}
        >
          <Accordion.AccordionPanel
            dataKey={assignmentFilterKey}
            className={cx("accordion-panel")}
            title={`Assignees by Variable Value (${assignmentFilterKey})`}
          >
            <KeyValueList
              data={assignmentFilterKeyValueData}
              color="neutral"
              variant="shaded"
            />
          </Accordion.AccordionPanel>
        </Accordion>
      ) : null}
    </>
  );
};
